/**
 * Teammate helpers (P5.4 Agent Teams) — pure functions shared by the
 * TeammateCard renderer, the socket handlers, and the reload-durability scan.
 *
 * The parent Agent tool_use (mode:'teammate') settles with a synthetic
 * tool_result carrying the DELEGATED marker: JSON {status:'delegated', name,
 * teammateTaskId} (built sandbox-side via buildDelegatedResultContent). The
 * web stores tool results as strings (streamingHandlers stringifies non-string
 * content), so the marker can arrive either as the bare JSON string or as a
 * serialized content-block array wrapping it — parse both.
 */

import {
  parseDelegatedResultContent,
  readSender,
  senderFromPeer,
  senderLabel,
} from "@bash/shared";
import type { Message, TaskState } from "@/stores/types";
import type { ToolUseBlock } from "@/types";

export interface DelegatedMarker {
  name: string;
  teammateTaskId: string;
  /**
   * Phase 2 (background helpers) — present only on a helper's marker. The
   * marker is the only spawn record a RELOAD has, so this is what opens a
   * helper lane rather than a teammate one after a refresh.
   */
  kind?: "helper";
  /** Phase 2 — what the MODEL read in place of a result ("Helper launched in
   *  the background."). Carried for completeness; no surface renders it. */
  note?: string;
  /**
   * R76 — the helper's brief, which TITLES its lane. Live it rides the spawn
   * event; after a reload this marker is the only record of it, so the rebuild
   * reads it from here or the card falls back to the minted handle.
   */
  description?: string;
  /**
   * Phase 3 (§4.6) — the person can message this helper. Same reasoning as the
   * description: after a reload this marker is the only record of it, so a lane
   * rebuilt without it comes back filtered out of the composer's @ list.
   */
  addressable?: true;
}

/** Parse a delegated-spawn marker out of a tool_use block's stored result. */
export function parseDelegatedMarker(result: unknown): DelegatedMarker | null {
  if (Array.isArray(result)) return parseFromBlockArray(result);
  if (typeof result !== "string" || !result) return null;

  const direct = parseDelegatedResultContent(result);
  if (direct) return direct;

  // The result may be a stringified tool_result content-block array
  // ([{type:'text', text:'<marker json>'}]) — unwrap and retry.
  try {
    const parsed: unknown = JSON.parse(result);
    if (Array.isArray(parsed)) return parseFromBlockArray(parsed);
  } catch {
    // Not JSON — a plain text result from a non-teammate tool.
  }
  return null;
}

function parseFromBlockArray(blocks: unknown[]): DelegatedMarker | null {
  for (const b of blocks) {
    if (
      b !== null &&
      typeof b === "object" &&
      (b as { type?: unknown }).type === "text" &&
      typeof (b as { text?: unknown }).text === "string"
    ) {
      const marker = parseDelegatedResultContent((b as { text: string }).text);
      if (marker) return marker;
    }
  }
  return null;
}

export interface ScannedSpawn extends DelegatedMarker {
  toolUseId: string;
}

/**
 * Reload durability — scan a task's loaded messages for delegated tool_results
 * so the teammate registry can be rebuilt client-side after a refresh/sync.
 */
export function scanDelegatedSpawns(messages: Message[]): ScannedSpawn[] {
  const spawns: ScannedSpawn[] = [];
  const seen = new Set<string>();
  for (const msg of messages) {
    if (msg.role !== "assistant") continue;
    for (const block of msg.content) {
      if (block.type !== "tool_use") continue;
      const tb = block as ToolUseBlock;
      const marker = parseDelegatedMarker(tb.result);
      if (!marker || seen.has(marker.teammateTaskId)) continue;
      seen.add(marker.teammateTaskId);
      spawns.push({ ...marker, toolUseId: tb.toolUseId });
    }
  }
  return spawns;
}

export type TeammateStatus = "needs_you" | "working" | "idle";

/** Live status of a teammate lane, derived from its hidden TaskState. Takes
 *  only the two fields it reads, so a surface holding those two (the lane card
 *  subscribes to them separately, never to the whole task) can ask the same
 *  question without a second copy of the rule. */
export function teammateStatus(
  task: Pick<TaskState, "phase" | "pendingInteraction"> | null | undefined
): TeammateStatus {
  if (!task) return "idle";
  if (task.pendingInteraction) return "needs_you";
  if (
    task.phase === "processing" ||
    task.phase === "waiting" ||
    task.phase === "interrupting"
  ) {
    return "working";
  }
  return "idle";
}

/**
 * Phase 2, ruling R82 — is this lane LIVE? A background helper that pauses on a
 * permission or a question is still alive, still holding a lane, and still
 * needs stopping; counting only `working` hid exactly the case where being
 * invisible costs the most, because it is the one that needs a human. Only a
 * lane that is OVER leaves the set.
 */
export function isLiveHelperStatus(status: TeammateStatus): boolean {
  return status === "working" || status === "needs_you";
}

/** A mention with something to say after it — the only shape that routes by default. */
const MENTION_WITH_BODY = /^@([^\s@]+)\s+([\s\S]+)$/;
/**
 * R15 — the same mention with the BODY made optional, for a send whose MESSAGE
 * is an attachment. Only the body: the lookahead keeps the SEPARATOR rule
 * intact, so a handle still has to end where a handle ends. `\s*` alone would
 * have let a body abut the handle whenever it opened with a character the
 * handle class cannot eat — in practice another `@` — and `@name@x hi` typed
 * with Bash in view and a file riding along would have left for the helper.
 */
const MENTION_ALONE = /^@([^\s@]+)(?=\s|$)\s*([\s\S]*)$/;

/**
 * Composer routing — parse a leading `@name ` prefix against the open task's
 * LIVE teammates. Returns the target teammateTaskId and the text with the
 * prefix stripped, or null when the mention doesn't match a live teammate
 * (falls through as plain text to the lead).
 *
 * `allowEmptyBody` (R15, spec 2026-09-06 §3.7) — accept `@name` with nothing
 * after it. The composer ALWAYS addresses the lane in view, so a screenshot
 * dropped on a helper's screen and sent with no caption arrives here as
 * `"@helper "`; refusing it delivered that to the LEAD carrying a stray handle.
 * Off by default, and it is the CALLER's to spend: only a send that actually
 * carries an image or a file may, so a bare `@researcher` typed with Bash in
 * view is still words, and still goes to Bash.
 */
export function parseTeammateMention(
  content: string,
  parentTask: Pick<TaskState, "teammates"> | null | undefined,
  options: { allowEmptyBody?: boolean } = {}
): { teammateTaskId: string; name: string; text: string } | null {
  const teammates = parentTask?.teammates;
  if (!teammates) return null;
  const match = (
    options.allowEmptyBody ? MENTION_ALONE : MENTION_WITH_BODY
  ).exec(content);
  if (!match) return null;
  const mention = match[1].toLowerCase();
  for (const [teammateTaskId, info] of Object.entries(teammates)) {
    if (info.name.toLowerCase() === mention) {
      return { teammateTaskId, name: info.name, text: match[2].trim() };
    }
  }
  return null;
}

/**
 * The `@name` a composer must open with to reach this teammate — the other half
 * of `parseTeammateMention` above, and it lives here so the two can never drift.
 *
 * The ROSTER name is what that parse matches. A lane also has a `title`, minted
 * `@name` at spawn, which is what surfaces show — but a title is a title, and a
 * server-written one still reads fine while matching nothing. A prefix built
 * from one is not a broken send: it is a message delivered to the LEAD that the
 * reader believed went to the teammate. `fallback` covers a row whose roster
 * entry has gone (which cannot route either, but must not render blank).
 */
export function teammateHandle(
  parentTask: Pick<TaskState, "teammates"> | null | undefined,
  teammateTaskId: string,
  fallback: string
): string {
  const name = parentTask?.teammates?.[teammateTaskId]?.name;
  return name ? `@${name}` : fallback;
}

/**
 * Peer attribution stamped on relayed lead↔teammate message rows
 * (content[0].peer — see the server's SUBAGENT_MESSAGE persist path).
 */
export interface PeerAttribution {
  direction: "to_teammate" | "to_lead";
  name: string;
  peerTaskId: string;
  /**
   * Phase 2 (D11) — why a background helper's REPORT arrived: completed,
   * stopped_by_user, turn_cap or failed. Absent on a teammate's report, and
   * that absence is how the two legs are told apart. Typed as the wire types
   * it (a plain string), so an unknown future reason still reads.
   */
  stopReason?: string;
  /**
   * Phase 3 (§4.5) — WHO sent this message: "lead" for the lead, a helper's own
   * handle for a helper. Absent on every pre-Phase-3 row, which reads as the
   * lead's. The delivered text is bare, so this is the only structured answer
   * the row label has to "who is talking".
   */
  from?: string;
}

/**
 * P5.6 coherence — is this to_lead row the AUTO-REPORT ECHO of a teammate's
 * final turn text? The sandbox wraps those in a fixed preamble. The same
 * content is already visible in the teammate's lane (its own thread) and in
 * the lead's synthesis, so the main thread suppresses the echo row entirely;
 * explicit ReportToLead updates (no wrapper) and failure notices stay visible.
 */
export function isAutoReportEcho(msg: Pick<Message, "content">): boolean {
  const peer = messagePeer(msg);
  if (peer?.direction !== "to_lead") return false;
  const first = msg.content[0] as { type?: string; text?: string } | undefined;
  if (first?.type !== "text" || typeof first.text !== "string") return false;
  return /^Teammate "[^"]+" finished its turn and reports:/.test(first.text);
}

/**
 * R79 (spec §5) — the reason THIS helper's report carried, read off the lead's
 * thread. The report lands on the LEAD (the helper's own lane never holds it),
 * attributed by peerTaskId. Scanned from the end so the newest report wins, and
 * undefined until one arrives.
 */
export function helperReportReason(
  leadMessages: Message[] | undefined,
  helperTaskId: string
): string | undefined {
  if (!leadMessages) return undefined;
  for (let i = leadMessages.length - 1; i >= 0; i--) {
    const peer = messagePeer(leadMessages[i]);
    if (peer?.direction === "to_lead" && peer.peerTaskId === helperTaskId)
      return peer.stopReason;
  }
  return undefined;
}

/**
 * R79 — the line an ended HELPER lane reads. Spec §5 forbids a lane reading
 * "finished" after a kill, so the ending comes from the REPORT's reason, never
 * from the lane merely going idle. No reason means no report has landed yet
 * (or the row predates D11), which reads as the clean finish it read before.
 *
 * The C1 belt: `endedInterrupted` is the lane's own terminal saying the run was cut
 * short (an AGENT_END footer marked interrupted, or the server's `user_interrupt`
 * stop). A report that DID arrive always wins; this only covers the ending whose
 * report never reached this client (minted with both sockets down, M7), which would
 * otherwise read "Helper finished" for a run the person killed.
 */
export function helperEndingLine(
  stopReason: string | undefined,
  endedInterrupted = false
): string {
  switch (stopReason) {
    case "stopped_by_user":
      return "Helper stopped";
    // The LEAD called this one off (StopHelper, spec 2026-09-05 §4.9). "Helper stopped"
    // beside a Stop button the person never pressed reads as their own click, so the lane
    // names who actually made the call.
    case "stopped_by_parent":
      return "Stopped by Bash";
    // Not the same ending as the one above, however alike they look from here: nobody
    // called this helper off, the account ran dry underneath it.
    case "credits_exhausted":
      return "Helper stopped, out of credits";
    case "turn_cap":
      return "Helper hit its turn limit";
    case "failed":
      return "Helper failed";
    default:
      return endedInterrupted ? "Helper stopped" : "Helper finished";
  }
}

/**
 * WHO wrote a row in a thread. THE reader — every surface goes through this one.
 *
 * Three outcomes, and the third is the point (spec 2026-09-12). Before this there were two,
 * "the person" and "someone named", and "the person" doubled as "nobody recorded a sender".
 * A screen cannot tell those apart, because both look like nothing, so anything unsigned
 * became the person's — and Bash's own instructions rendered wearing their name (founder,
 * 09-12: "I never sent those messages to teammate, they are sent by bash").
 *
 * `unknown` is what lets a surface decline to claim an author. On a lane thread that is the
 * honest reading of an unsigned row, and it is strictly better than asserting something
 * false. It also makes the remaining unsigned writers VISIBLE rather than silently
 * attributed, which is how the last of them get found.
 */
export type RowAuthor =
  { kind: "person" } | { kind: "named"; label: string } | { kind: "unknown" };

const PERSON: RowAuthor = { kind: "person" };
const UNKNOWN: RowAuthor = { kind: "unknown" };

/**
 * `lane` says whether the person is the ONLY possible writer here.
 *
 *  · false (their own task) — an unsigned row is theirs. Safe: nothing else writes there,
 *    and every message they have ever sent predates the stamp.
 *  · true (a lane) — the lead, a sibling helper and the person all write, so an unsigned
 *    row names nobody and the surface says nothing.
 */
export function rowAuthor(
  msg: Pick<Message, "content">,
  opts: { lane: boolean }
): RowAuthor {
  const signed = readSender(msg.content);
  if (signed) {
    const label = senderLabel(signed);
    return label === null ? PERSON : { kind: "named", label };
  }
  // Legacy: rows written before the stamp still carry the routing record, and on the
  // relay channel that is enough to name the writer. Kept so history reads correctly
  // rather than going blank the day the field shipped.
  const peer = messagePeer(msg);
  if (peer) {
    const label = senderLabel(
      senderFromPeer(peer.direction, peer.name, peer.from)
    );
    if (label !== null) return { kind: "named", label };
  }
  return opts.lane ? UNKNOWN : PERSON;
}

/**
 * Did BASH write this row? Asked directly rather than pattern-matched, because the one
 * surface that needs it is an ATTRIBUTION ("Bash asked") and an attribution that guesses
 * is the defect. Falls back to the routing record for rows written before the stamp.
 */
export function isFromBash(msg: Pick<Message, "content">): boolean {
  const signed = readSender(msg.content);
  if (signed) return signed.kind === "bash";
  const peer = messagePeer(msg);
  return peer
    ? senderFromPeer(peer.direction, peer.name, peer.from).kind === "bash"
    : false;
}

/** Read the peer attribution off a message, if present. */
export function messagePeer(
  msg: Pick<Message, "content">
): PeerAttribution | null {
  const first = msg.content[0] as { peer?: PeerAttribution } | undefined;
  const peer = first?.peer;
  if (!peer || !peer.direction || !peer.name) return null;
  return peer;
}
